import React, {PureComponent} from 'react';
import Image from 'app/helpers/Image';
import BuyButton from 'app/components/shop/lootboxes/buy_button';
import ShopItemDiscount from './shop_item_discount';
import ShopItemMeta from 'app/components/shop/lootboxes/ShopItemMeta';
import {goToShopLootbox, goToShopOtherItem} from 'app/utils/app_links';
import {shopLootboxStore} from 'app/store/global';
import {translatedName} from 'app/utils/translations_helper';
import I18n from 'app/i18n';
import {formatShopPoints, isUSD, shopItemPrice} from 'app/utils/ShopItemPriceFormat';
import {otherItemName} from 'app/components/my_profile/artifacts/otherItemName';
import {isLootBox} from 'app/utils/isLootBox';
import {coolOffLootbox} from 'app/utils/isCoolOffActiveFor';
import {isDesktop} from 'app/utils';
import ShopItemInterface from 'app/interfaces/shop/ShopItemInterface';
import {ShopUserInterface} from 'app/store/ShopStore';
import UserInterface from 'app/interfaces/UserInterface';

export interface ShopItemProps extends ShopItemInterface {
    user: ShopUserInterface;
    game?: string;
    langs?: {[key: string]: string};
    isCasinoPage?: boolean;
    hideMeta?: boolean;
    priceInsideButton?: boolean;
    scrollToTopBlock?: () => void;
    startAnimationOnNonShopPage?: () => void;
    afterBuy?: () => void;
}

export default class ShopItem extends PureComponent<ShopItemProps> {
    containerRef = React.createRef<HTMLDivElement>();

    moreDetailsHandler = (e?: React.MouseEvent): void => {
        e && e.preventDefault();
        const {id, recipe, currency, description, type_shop, isCasinoPage, scrollToTopBlock} = this.props;
        const shouldKeepCasinoFilterPosition = isCasinoPage && !isDesktop();

        if (type_shop === 'lootbox' && recipe) {
            if (shouldKeepCasinoFilterPosition) {
                scrollToTopBlock?.();
            }

            goToShopLootbox(recipe.slug, !isUSD(currency || ''), {
                isCasinoPage,
                preventScroll: Boolean(isCasinoPage)
            });

            if (isCasinoPage && !shouldKeepCasinoFilterPosition) {
                scrollToTopBlock?.();
            }
        }
        if (type_shop === 'other' && description) {
            goToShopOtherItem(id, !isUSD(currency || ''));
        }
    };

    afterBuy = (): void => {
        const {recipe, type_shop, startAnimationOnNonShopPage} = this.props;

        if (type_shop === 'lootbox' && recipe) {
            shopLootboxStore.updateOpenNowStatus(true);
            startAnimationOnNonShopPage && startAnimationOnNonShopPage();
        }
        this.moreDetailsHandler();
    };

    get gameClass(): string {
        const {game} = this.props;

        return typeof game === 'string'
            ? (game === '' ? 'game' : 'game_') + game.toLowerCase().replace(/ |:\s*/gu, '_')
            : '';
    }

    nameItem = (): string => {
        const {recipe, title, type_shop, langs} = this.props;

        if (type_shop === 'lootbox') {
            return translatedName(recipe);
        }
        if (type_shop === 'other') {
            return otherItemName({langs, title});
        }
        return title;
    };

    coolOffEnabled = (): boolean => {
        const {user, type_shop} = this.props;

        return coolOffLootbox(user as unknown as UserInterface) && isLootBox(type_shop);
    };

    get shopItemPriceProps(): {cost_money: number; cost_points: number; currency: string} {
        const {cost_money, cost_points, currency = ''} = this.props;

        return {
            cost_money: Number(cost_money),
            cost_points: Number(cost_points),
            currency
        };
    }

    renderPointsPrice = (): React.ReactNode => {
        const {cost_points, currency = ''} = this.props;

        if (isUSD(currency)) {
            return shopItemPrice(this.shopItemPriceProps);
        }

        return <>
            <span className="market__lootbox--points-label">{I18n.t('user_points')}</span>
            <span className="market__lootbox--points-value">{formatShopPoints(cost_points)}</span>
        </>;
    };

    renderBuyActions = (): React.ReactNode => {
        const {priceInsideButton} = this.props;

        if (priceInsideButton) {
            return <div className="market__lootbox--actions">
                <BuyButton
                    {...this.props}
                    afterBuy={this.afterBuy}
                    with_confirmation={true}
                    show_loading={false}
                    className="market__lootbox--buy"
                    disabled={this.coolOffEnabled()}
                />
            </div>;
        }

        return <div className="market__lootbox--footer">
            <span className="market__lootbox--points">
                {this.renderPointsPrice()}
            </span>
            <div className="market__lootbox--actions">
                <BuyButton
                    {...this.props}
                    afterBuy={this.afterBuy}
                    with_confirmation={true}
                    show_loading={false}
                    hidePrice={true}
                    className="market__lootbox--buy"
                    disabled={this.coolOffEnabled()}
                />
            </div>
        </div>;
    };

    render(): React.ReactNode {
        const {type_shop, sale, sale_money, currency = '', image, hideMeta, recipe, title} = this.props;
        const showMeta = type_shop === 'lootbox' && !hideMeta;

        return <div className="market__lootbox">
            <div className="market__lootbox--img-wrap">
                <div
                    className={`market__lootbox--img ${this.gameClass}`}
                    onClick={this.moreDetailsHandler}
                    ref={this.containerRef}
                >
                    <span className="market__lootbox--img-span">
                        <Image
                            imageDomain
                            image={image}
                            title={title}
                        />
                    </span>
                    <ShopItemDiscount
                        sale={sale}
                        sale_money={sale_money}
                        currency={currency}
                    />
                </div>
            </div>
            <div className="market__lootbox--desc">
                <div className="market__lootbox--desc__content">
                    <h3 className="market__lootbox--title">{this.nameItem()}</h3>
                    {showMeta && <ShopItemMeta
                        loot={recipe?.loot}
                        maxCalculatedPrice={recipe?.max_calculated_price}
                    />}
                </div>
                {this.renderBuyActions()}
            </div>
        </div>;
    }
}
